import { loadAuthContext } from '../auth/auth.middleware.js';
import type { AuthContext } from '../auth/auth.types.js';
import {
  registerImportEntity,
  type ImportColumn,
  type ImportContext,
  type ImportEntity,
} from '../csv/csv.registry.js';
import { issuesToErrors } from '../parties/parties.import.js';
import { parseDateCell } from '../sales/sales.import.js';
import {
  LEAD_SOURCES,
  SERVICE_INTERESTS,
  createLead,
  leadInputSchema,
  moveLead,
  scheduleLeadFollowUp,
  updateLead,
  type LeadInput,
} from './crm.service.js';

interface LeadRow {
  input: LeadInput;
  ownerEmail: string | null;
  pipeline: string | null;
  stage: string | null;
  status: string | null;
  lostReason: string | null;
}

const columns: ImportColumn[] = [
  {
    key: 'name',
    label: 'Lead / company name',
    required: true,
    example: 'Kampala Traders Ltd',
    aliases: ['company', 'lead'],
  },
  { key: 'contact_name', label: 'Contact person', example: 'Jane Nakato', aliases: ['contact'] },
  { key: 'phone', label: 'Phone', example: '0772123456' },
  { key: 'email', label: 'Email', example: 'jane@kampalatraders.example' },
  { key: 'source', label: 'Source', example: 'referral', aliases: ['lead_source'] },
  {
    key: 'service_interest',
    label: 'Service interest',
    example: 'software',
    aliases: ['interest'],
  },
  { key: 'estimated_value', label: 'Estimated value', example: '5000000', aliases: ['value'] },
  { key: 'currency', label: 'Currency', example: 'UGX' },
  {
    key: 'owner_email',
    label: 'Owner (user email)',
    example: 'sam@roovers.example',
    aliases: ['owner'],
  },
  { key: 'pipeline', label: 'Pipeline', example: 'Software Projects' },
  { key: 'stage', label: 'Stage', example: 'Contacted' },
  { key: 'status', label: 'Status (open, won, lost)', example: 'open' },
  { key: 'lost_reason', label: 'Lost reason', example: '' },
  {
    key: 'next_follow_up',
    label: 'Next follow-up date',
    example: '30/03/2026',
    aliases: ['follow_up'],
  },
  { key: 'tags', label: 'Tags (separated by ;)', example: 'priority;kampala' },
  { key: 'notes', label: 'Notes', example: '' },
];

async function authFor(ctx: ImportContext): Promise<AuthContext> {
  const a = await loadAuthContext(ctx.tx, ctx.userId, ctx.companyId);
  if (!a) throw new Error('User not found');
  return a;
}

async function resolve(ctx: ImportContext, v: LeadRow) {
  const errors: string[] = [];
  let ownerId: number | undefined;
  if (v.ownerEmail) {
    const u = await ctx.tx.user.findFirst({
      where: { companyId: ctx.companyId, email: v.ownerEmail, status: 'active' },
    });
    if (!u) errors.push(`Owner: no active user with email ${v.ownerEmail}`);
    else ownerId = u.id;
  }
  let pipelineId: number | undefined;
  let stageId: number | undefined;
  let stageKind: string | undefined;
  if (v.pipeline) {
    const p = await ctx.tx.pipeline.findFirst({
      where: { companyId: ctx.companyId, name: v.pipeline, active: true },
      include: { stages: true },
    });
    if (!p) errors.push(`Pipeline "${v.pipeline}" does not exist`);
    else {
      pipelineId = p.id;
      if (v.stage) {
        const s = p.stages.find((x) => x.name.toLowerCase() === v.stage!.toLowerCase());
        if (!s) errors.push(`Stage "${v.stage}" is not in pipeline "${p.name}"`);
        else {
          stageId = s.id;
          stageKind = s.kind;
        }
      }
    }
  } else if (v.stage) {
    errors.push('Stage needs a pipeline');
  }
  if (stageKind === 'lost' && (!v.lostReason || v.lostReason.length < 3))
    errors.push('A lost lead needs a lost reason');
  return { errors, ownerId, pipelineId, stageId };
}

const entity: ImportEntity<LeadRow> = {
  key: 'leads',
  label: 'Leads',
  importPermission: 'crm.lead.import',
  columns,
  parseRow(raw) {
    const errors: string[] = [];
    const num = (raw.estimated_value ?? '').replace(/,/g, '').trim() || '0';
    const source =
      (raw.source ?? '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_') || 'other';
    if (!(LEAD_SOURCES as readonly string[]).includes(source))
      errors.push(`Source must be one of ${LEAD_SOURCES.join(', ')}`);
    const interest = (raw.service_interest ?? '').trim().toLowerCase();
    if (interest && !(SERVICE_INTERESTS as readonly string[]).includes(interest))
      errors.push(`Service interest must be one of ${SERVICE_INTERESTS.join(', ')}`);
    const status = (raw.status ?? '').trim().toLowerCase() || null;
    if (status && !['open', 'won', 'lost'].includes(status))
      errors.push('Status must be open, won or lost');
    const follow = parseDateCell(raw.next_follow_up, 'Next follow-up date', errors, false);
    if (errors.length) return { ok: false, errors };
    const parsed = leadInputSchema.safeParse({
      name: raw.name,
      contactName: raw.contact_name?.trim() || null,
      phone: raw.phone?.trim() || null,
      email: raw.email?.trim() || null,
      source,
      serviceInterest: interest || null,
      estimatedValue: num,
      currency: raw.currency?.trim() || 'UGX',
      tags: (raw.tags ?? '')
        .split(';')
        .map((t) => t.trim())
        .filter(Boolean),
      notes: raw.notes?.trim() || null,
      nextFollowUpDate: follow,
    });
    if (!parsed.success)
      return {
        ok: false,
        errors: issuesToErrors(parsed.error, {
          name: 'Name',
          email: 'Email',
          estimatedValue: 'Estimated value',
        }),
      };
    return {
      ok: true,
      value: {
        input: parsed.data,
        ownerEmail: raw.owner_email?.trim().toLowerCase() || null,
        pipeline: raw.pipeline?.trim() || null,
        stage: raw.stage?.trim() || null,
        status,
        lostReason: raw.lost_reason?.trim() || null,
      },
    };
  },
  rowKey: (v) => (v.input.email ? `lead:${v.input.email}` : `lead:${v.input.name.toLowerCase()}`),
  async validate(ctx, v) {
    return (await resolve(ctx, v)).errors;
  },
  async findExisting(ctx, v) {
    const where = v.input.email ? { email: v.input.email } : { name: v.input.name };
    const l = await ctx.tx.lead.findFirst({ where: { companyId: ctx.companyId, ...where } });
    return l ? { id: l.id, snapshot: null } : null;
  },
  async create(ctx, v) {
    const auth = await authFor(ctx);
    const r = await resolve(ctx, v);
    let lead = await createLead(ctx.tx, auth, {
      ...v.input,
      ownerId: r.ownerId,
      pipelineId: r.pipelineId,
      stageId: r.stageId,
    });
    if (v.status && v.status !== 'open' && lead.status !== v.status) {
      const stage = await ctx.tx.pipelineStage.findFirst({
        where: { pipelineId: lead.pipelineId, kind: v.status },
      });
      if (stage) lead = await moveLead(ctx.tx, auth, lead.id, stage.id, v.lostReason ?? undefined);
    }
    return { id: lead.id };
  },
  async update(ctx, id, v) {
    const auth = await authFor(ctx);
    const r = await resolve(ctx, v);
    await updateLead(ctx.tx, auth, id, { ...v.input, ownerId: r.ownerId });
  },
  async restore() {
    /* update mode is not individually reversible; rollback removes only created leads */
  },
  async remove(ctx, id) {
    await ctx.tx.lead.deleteMany({ where: { id, companyId: ctx.companyId } });
    return null;
  },
};

registerImportEntity(entity);

/** After a committed import, schedule follow-up reminders for the leads that carry a date. */
export { scheduleLeadFollowUp };
